import { cleanParams, del, downloadFile, get, getPaginated, post, put } from './client';
import type {
  AuditLogFilters,
  AuditLogItem,
  AuthUser,
  BulkImportResult,
  BulkImportUpload,
  BulkInvoiceResult,
  ClientBillingSnapshot,
  ClientDetail,
  ClientListItem,
  DashboardResponse,
  ExportFormat,
  InvoiceDetail,
  InvoiceListItem,
  LoginResponse,
  MikrotikActiveSession,
  MikrotikPppoeSecret,
  MikrotikPppoeInterface,
  MikrotikPppProfile,
  MikrotikResource,
  Paginated,
  PaymentListItem,
  PaymentMethodOption,
  RecordPaymentResult,
  ServicePlan,
} from './types';

// --- Authentication ---------------------------------------------------------

export const authApi = {
  login: (username: string, password: string) =>
    post<LoginResponse>('/auth/login', { username, password }),
  logout: () => post<null>('/auth/logout'),
  me: () => get<AuthUser>('/auth/me'),
  forgotPassword: (email: string) => post<null>('/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string, confirmPassword: string) =>
    post<null>('/auth/reset-password', { token, password, confirmPassword }),
  changePassword: (currentPassword: string, newPassword: string, confirmPassword: string) =>
    post<null>('/auth/change-password', { currentPassword, newPassword, confirmPassword }),
};

// --- Dashboard --------------------------------------------------------------

export const dashboardApi = {
  get: () => get<DashboardResponse>('/dashboard'),
};

// --- Clients ----------------------------------------------------------------

export interface ClientListParams {
  page: number;
  pageSize: number;
  search?: string;
  status?: string;
  lcp?: string;
  nap?: string;
  sort?: string;
  order?: 'asc' | 'desc';
}

export interface ClientFormValues {
  customerType: 'RESIDENTIAL' | 'BUSINESS';
  firstName?: string;
  middleName?: string;
  lastName?: string;
  businessName?: string;
  contactPerson?: string;
  mobileNumber: string;
  email?: string;
  notes?: string;
  isActive?: boolean;
  address: {
    addressLine1: string;
    addressLine2?: string;
    barangay?: string;
    municipality?: string;
    province?: string;
    postalCode?: string;
  };
  subscription?: {
    servicePlanId: number;
    status: string;
    billingDay: number;
    installationDate?: string;
    activationDate?: string;
    pppoeUsername?: string;
    installationFee?: number;
    referralFee?: number;
    installerCommission?: number;
    agentReferral?: string;
    installerNames?: string;
    smt?: string;
    lcp?: string;
    nap?: string;
    portNumber?: string;
    autoSuspendEnabled?: boolean;
    gracePeriodDays?: number;
    pppoePassword?: string;
    routerSerialNumber?: string;
    onuSerialNumber?: string;
    remarks?: string;
  };
}

export const clientsApi = {
  list: (params: ClientListParams): Promise<Paginated<ClientListItem>> =>
    getPaginated<ClientListItem>('/clients', { params: cleanParams({ ...params }) }),
  detail: (id: number) => get<ClientDetail>(`/clients/${id}`),
  create: (values: ClientFormValues) => post<{ id: number; clientNumber: string }>('/clients', values),
  import: (upload: BulkImportUpload) => post<BulkImportResult>('/clients/import', upload),
  update: (id: number, values: ClientFormValues) =>
    put<{ id: number; clientNumber: string }>(`/clients/${id}`, values),
  setDueDate: (id: number, dueDate: string) =>
    put<{ id: number; subscriptionNumber: string; nextDueDate: string }>(`/clients/${id}/due-date`, { dueDate }),
  remove: (id: number) => del<null>(`/clients/${id}`),
};

// --- Subscriptions ----------------------------------------------------------

export interface SubscriptionFormValues {
  clientId?: number;
  servicePlanId?: number;
  serviceAddressId?: number;
  status?: string;
  port?: string;
  billingDay?: number;
  branch?: string;
  installationDate?: string;
  activationDate?: string;
  autoSuspendEnabled?: boolean;
  gracePeriodDays?: number;
  nextDueDate?: string;
  smt?: string;
  lcp?: string;
  nap?: string;
  portNumber?: string;
  pppoeUsername?: string;
  pppoePassword?: string;
  routerSerialNumber?: string;
  onuSerialNumber?: string;
  remarks?: string;}

export const subscriptionsApi = {
  create: (values: SubscriptionFormValues) =>
    post<{ id: number; subscriptionNumber: string }>('/subscriptions', values),
  update: (id: number, values: SubscriptionFormValues) =>
    put<{ id: number; subscriptionNumber: string; status: string }>(
      `/subscriptions/${id}`,
      values,
    ),
  setDueDate: (id: number, dueDate: string) =>
    put<{ id: number; subscriptionNumber: string; status: string; nextDueDate: string }>(`/subscriptions/${id}/due-date`, { dueDate }),
};

// --- Service plans ----------------------------------------------------------

export interface ServicePlanFormValues {
  planCode: string;
  planName: string;
  downloadSpeedMbps: number;
  uploadSpeedMbps: number;
  monthlyFee: number;
  description?: string;
  mikrotikRemoteAddress?: string;
  mikrotikAddressList?: string;
}

export const servicePlansApi = {
  list: (includeRetired = false) =>
    get<ServicePlan[]>('/service-plans', { params: { includeRetired: String(includeRetired) } }),
  import: (upload: BulkImportUpload) => post<BulkImportResult>('/service-plans/import', upload),
  create: (values: ServicePlanFormValues) => post<ServicePlan>('/service-plans', values),
  update: (id: number, values: ServicePlanFormValues) => put<ServicePlan>(`/service-plans/${id}`, values),
  replace: (id: number, values: ServicePlanFormValues) =>
    post<ServicePlan>(`/service-plans/${id}/replace`, values),
  remove: (id: number) => del<null>(`/service-plans/${id}`),
};

// --- Invoices ---------------------------------------------------------------

export interface InvoiceListParams {
  page: number;
  pageSize: number;
  status?: string;
  port?: string;
  clientId?: number;
  subscriptionId?: number;
  overdueOnly?: string;
  search?: string;
  sort?: string;
  order?: 'asc' | 'desc';
}

export const invoicesApi = {
  list: (params: InvoiceListParams): Promise<Paginated<InvoiceListItem>> =>
    getPaginated<InvoiceListItem>('/invoices', { params: cleanParams({ ...params }) }),
  detail: (id: number) => get<InvoiceDetail>(`/invoices/${id}`),
  create: (values: { subscriptionId: number; billingYear?: number; billingMonth?: number; notes?: string }) =>
    post<InvoiceListItem>('/invoices', values),
  generateBatch: (values: { billingYear: number; billingMonth: number; servicePlanId?: number }) =>
    post<BulkInvoiceResult>('/invoices/generate', values),
  download: (id: number, invoiceNumber: string) => downloadFile(`/invoices/${id}/pdf`, { cacheBust: Date.now() }, `${invoiceNumber}.pdf`),
  void: (id: number, reason: string) =>
    put<{ id: number; invoiceNumber: string }>(`/invoices/${id}/void`, { reason }),
};

// --- Payment methods --------------------------------------------------------

export const paymentMethodsApi = {
  list: (includeInactive = false) => get<PaymentMethodOption[]>('/payment-methods', { params: { includeInactive: String(includeInactive) } }),
  create: (values: { code: string; name: string }) => post<PaymentMethodOption>('/payment-methods', values),
  update: (id: number, values: { name?: string; isActive?: boolean }) => put<PaymentMethodOption>(`/payment-methods/${id}`, values),
  remove: (id: number) => del<null>(`/payment-methods/${id}`),
};
// --- Payments ---------------------------------------------------------------

export interface PaymentListParams {
  page: number;
  pageSize: number;
  clientId?: number;
  paymentMethod?: string;
  dateFrom?: string;
  dateTo?: string;
  includeVoid?: string;
  search?: string;
  sort?: string;
  order?: 'asc' | 'desc';
}

export interface RecordPaymentValues {
  clientId: number;
  paymentMethod: string;
  paymentDate: string;
  amountReceived: number;
  rebate?: number;
  reactivateSubscription?: boolean;
  reactivationOnly?: boolean;
  reconnectionFee?: number;
  referenceNumber?: string;
  remarks?: string;
}

export const paymentsApi = {
  list: (params: PaymentListParams): Promise<Paginated<PaymentListItem>> =>
    getPaginated<PaymentListItem>('/payments', { params: cleanParams({ ...params }) }),
  detail: (id: number) => get<PaymentListItem>(`/payments/${id}`),
  snapshot: (clientId: number) =>
    get<ClientBillingSnapshot>(`/payments/client-snapshot/${clientId}`),
  record: (values: RecordPaymentValues) => post<RecordPaymentResult>('/payments', values),
  void: (id: number, reason: string) =>
    put<{ id: number; paymentNumber: string }>(`/payments/${id}/void`, { reason }),
};

// --- Audit logs -------------------------------------------------------------

export interface AuditLogParams {
  page: number;
  pageSize: number;
  userId?: number;
  module?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

export const auditLogsApi = {
  list: (params: AuditLogParams): Promise<Paginated<AuditLogItem>> =>
    getPaginated<AuditLogItem>('/audit-logs', { params: cleanParams({ ...params }) }),
  filters: () => get<AuditLogFilters>('/audit-logs/filters'),
};

// --- Exports ----------------------------------------------------------------

export const exportsApi = {
  clients: (format: ExportFormat, params: Record<string, unknown>) =>
    downloadFile('/export/clients', cleanParams({ ...params, format }), `clients.${format}`),
  payments: (format: ExportFormat, params: Record<string, unknown>) =>
    downloadFile('/export/payments', cleanParams({ ...params, format }), `payments.${format}`),
  invoices: (format: ExportFormat, params: Record<string, unknown>) =>
    downloadFile('/export/invoices', cleanParams({ ...params, format }), `invoices.${format}`),
};

// --- Settings ---------------------------------------------------------------

export const settingsApi = {
  get: () => get<Record<string, string>>('/settings'),
  updatePrefixes: (values: { client_prefix: string; invoice_prefix: string; payment_prefix: string }) => put<Record<string, string>>('/settings/prefixes', values),
};

// --- MikroTik ---------------------------------------------------------------

export interface CreateMikrotikPppoeSecretValues {
  name: string;
  password: string;
  profile: string;
  service?: string;
  comment?: string;
  disabled?: boolean;
}

export interface MikrotikServicePlanOptions {
  remoteAddresses: string[];
  addressLists: string[];
}

export interface MikrotikCrmSyncResult {
  secrets: number;
  interfaces: number;
  profiles: number;
  clientsCreated: number;
  subscriptionsCreated: number;
  subscriptionsUpdated: number;
  servicePlansCreated: number;
  servicePlansUpdated: number;
  skippedSecrets: number;
  profilesPushed: number;
  secretsPushed: number;
  pushFailures: number;
}

export const mikrotikApi = {
  syncCrm: () => post<MikrotikCrmSyncResult>('/mikrotik/sync-crm'),
  servicePlanOptions: () => get<MikrotikServicePlanOptions>('/mikrotik/service-plan-options'),
  resource: () => get<MikrotikResource>('/mikrotik/resource'),
  secrets: (search?: string) =>
    get<MikrotikPppoeSecret[]>('/mikrotik/pppoe/secrets', {
      params: cleanParams({ search }),
    }),
  interfaces: (search?: string) =>
    get<MikrotikPppoeInterface[]>('/mikrotik/pppoe/interfaces', { params: cleanParams({ search }) }),
  profiles: () => get<MikrotikPppProfile[]>('/mikrotik/ppp/profiles'),
  enableInterface: (id: string) =>
    post<MikrotikPppoeInterface>(`/mikrotik/pppoe/interfaces/${encodeURIComponent(id).replace(/\*/g, '%2A')}/enable`),
  disableInterface: (id: string) =>
    post<MikrotikPppoeInterface>(`/mikrotik/pppoe/interfaces/${encodeURIComponent(id).replace(/\*/g, '%2A')}/disable`),
  active: (search?: string) =>
    get<MikrotikActiveSession[]>('/mikrotik/pppoe/active', {
      params: cleanParams({ search }),
    }),
  createSecret: (values: CreateMikrotikPppoeSecretValues) =>
    post<MikrotikPppoeSecret>('/mikrotik/pppoe/secrets', values),
  enableSecret: (id: string) =>
    post<MikrotikPppoeSecret>(`/mikrotik/pppoe/secrets/${encodeURIComponent(id).replace(/\*/g, '%2A')}/enable`),
  disableSecret: (id: string) =>
    post<MikrotikPppoeSecret>(`/mikrotik/pppoe/secrets/${encodeURIComponent(id).replace(/\*/g, '%2A')}/disable`),
  disconnectSession: (id: string) =>
    post<null>(`/mikrotik/pppoe/active/${encodeURIComponent(id).replace(/\*/g, '%2A')}/disconnect`),
};





export interface Organization {
  id: number;
  name: string;
  slug: string;
  isActive: boolean;
  admin: OrganizationAdmin | null;
  mikrotik: OrganizationMikrotik | null;
}
export interface OrganizationAdmin {
  id: number; username: string; firstName: string; lastName: string;
  email: string; role: 'ORG_ADMIN'; isActive: boolean;
}
export interface OrganizationMikrotik {
  id: number; name: string; baseUrl: string; username: string;
  tlsVerify: boolean; isActive: boolean;
}
export interface AdminValues {
  username: string; password?: string; firstName: string;
  lastName: string; email: string; isActive: boolean;
}
export interface MikrotikValues {
  name: string; baseUrl: string; username: string;
  password?: string; tlsVerify: boolean; isActive: boolean;
}
export const platformApi = {
  organizations: () => get<Organization[]>('/platform/organizations'),
  createOrganization: (values: { name: string; slug: string }) =>
    post<Organization>('/platform/organizations', values),
  updateOrganization: (id: number, values: Partial<{ name: string; slug: string; isActive: boolean }>) =>
    put<Organization>('/platform/organizations/' + id, values),
  admin: (organizationId: number) =>
    get<OrganizationAdmin | null>('/platform/organizations/' + organizationId + '/admin'),
  saveAdmin: (organizationId: number, values: AdminValues) =>
    put<OrganizationAdmin>('/platform/organizations/' + organizationId + '/admin', values),
  mikrotik: (organizationId: number) =>
    get<OrganizationMikrotik | null>('/platform/organizations/' + organizationId + '/mikrotik'),
  saveMikrotik: (organizationId: number, values: MikrotikValues) =>
    put<OrganizationMikrotik>('/platform/organizations/' + organizationId + '/mikrotik', values),
};
export type OrganizationUserRole = 'MANAGER' | 'BILLING' | 'SUPPORT' | 'VIEWER';
export interface OrganizationUser {
  id: number; username: string; firstName: string; lastName: string;
  email: string; role: OrganizationUserRole; isActive: boolean;
}
export const organizationUsersApi = {
  list: () => get<OrganizationUser[]>('/organization/users'),
  create: (values: { username: string; password: string; firstName: string; lastName: string; email: string; role: OrganizationUserRole }) =>
    post<OrganizationUser>('/organization/users', values),
  update: (id: number, values: { role?: OrganizationUserRole; isActive?: boolean }) =>
    put<OrganizationUser>('/organization/users/' + id, values),
  resetPassword: (id: number, password: string) =>
    put<null>('/organization/users/' + id + '/password', { password }),
};

